import { useMemo, useState } from "react"
import classNames from "classnames"
import { ClickAwayListenerV2 } from "@nova/component-click-away-listener"
import { Dropdown } from "@nova/component-dropdown"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { Checkbox, TriCheckbox } from "@nova/component-checkbox"
import { Input, InputOutlined } from "@nova/ds-component-input"
import { ToggleSwitch } from "@nova/component-toggle-switch"
import * as Icons from "@nova/component-icons"
import { IconButton } from "@nova/component-btn"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { updateForm, selectForm } from "features/pilot-tracking/currency/slice"
import { CurrencyPolicyMetric, CurrencyPolicyRecipientInput, MemberExtended } from "@nova/schemas"
import {
    DroneCategory,
    droneCategories,
    getDroneCategory,
    getDroneCategoryDisplayName,
    normalizeDroneMake,
    normalizeDroneModel,
} from "@nova/constants"
import { useAircraft } from "features/pilot-tracking/aircraft/AircraftQueries"
import { pluralize } from "@nova/util-strings"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { RenamableText } from "features/data-management/workspace/TODO-put-in-package/RenamableText"
import { selectAllRegisteredMembers } from "features/entity-caching/members/UserManagementSlice"
import { isDefined } from "nodash"

const COLLAPSED_USER_COUNT = 6

const droneCategoryLabel = (key: string) =>
    key in droneCategories ? getDroneCategoryDisplayName(key as DroneCategory) : key

const useWorkspaceDroneCategories = (selected: string[]) => {
    const { aircraft } = useAircraft()
    return useMemo(() => {
        const categories = new Set<string>(selected)
        for (const a of aircraft) {
            const make = normalizeDroneMake(a.manufacturer ?? "")
            if (!make) continue
            const model = a.model ? normalizeDroneModel(make, a.model) : undefined
            if (!model) continue
            categories.add(getDroneCategory(make, model))
        }
        return [...categories]
            .map((c) => ({ label: droneCategoryLabel(c), value: c }))
            .toSorted((a, b) => a.label.localeCompare(b.label))
    }, [aircraft, selected])
}

const DroneTypeMultiSelect = ({
    value,
    onChange,
}: {
    value: string[]
    onChange: (v: string[]) => void
}) => {
    const categories = useWorkspaceDroneCategories(value)
    const [isOpen, setIsOpen] = useState(false)
    const [search, setSearch] = useState("")

    const filtered = categories.filter((c) => c.label.toLowerCase().includes(search.toLowerCase()))

    const triggerLabel =
        value.length === 0
            ? "All drone types"
            : value.length === 1
              ? droneCategoryLabel(value[0])
              : `${value.length} drone types`

    const toggle = (key: string) => {
        if (value.includes(key)) {
            onChange(value.filter((v) => v !== key))
        } else {
            onChange([...value, key])
        }
    }

    const allFilteredSelected =
        filtered.length > 0 && filtered.every((c) => value.includes(c.value))

    const toggleAll = () => {
        if (allFilteredSelected) {
            const filteredKeys = new Set(filtered.map((c) => c.value))
            onChange(value.filter((v) => !filteredKeys.has(v)))
        } else {
            onChange([...new Set([...value, ...filtered.map((c) => c.value)])])
        }
    }

    return (
        <Dropdown onOpenChange={setIsOpen}>
            <Dropdown.Trigger
                as="button"
                className={`flex justify-between items-center border pl-4 py-2 pr-2 text-left w-full rounded-lg bg-gray-700 ${isOpen ? "border-blue-200" : "border-transparent"}`}
            >
                <span className="text-gray-0">{triggerLabel}</span>
                {isOpen ? (
                    <Icons.ArrowDropUp className="text-gray-300" />
                ) : (
                    <Icons.ArrowDropdown className="text-gray-300" />
                )}
            </Dropdown.Trigger>
            <Dropdown.Menu matchTriggerWidth className="p-4 pb-1">
                <div className="py-2 flex items-center gap-3">
                    <span className="text-gray-300 text-sm shrink-0">Search</span>
                    <InputOutlined
                        placeholder="Search drone types"
                        className="text-white"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <button
                        type="button"
                        className="text-white text-sm whitespace-nowrap shrink-0"
                        onClick={toggleAll}
                    >
                        {allFilteredSelected ? "Deselect All" : "Select All"}
                    </button>
                </div>
                <ScrollableArea className="flex flex-col max-h-48">
                    {filtered.map((c) => (
                        <Dropdown.Item
                            key={c.value}
                            clickBehaviour="keep-open"
                            className="flex items-center gap-3 py-2"
                            onClick={() => toggle(c.value)}
                        >
                            <TriCheckbox
                                state={value.includes(c.value) ? "checked" : "unchecked"}
                                onClick={() => toggle(c.value)}
                            />
                            <span className="text-white text-sm">{c.label}</span>
                        </Dropdown.Item>
                    ))}
                </ScrollableArea>
            </Dropdown.Menu>
        </Dropdown>
    )
}

const FormRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-start gap-6">
        <div className="w-40 min-w-40 pt-2">
            <h5 className="text-white">{label}</h5>
        </div>
        <div className="flex-1">{children}</div>
    </div>
)

const UserAvatar = ({ userId, fullName }: { userId: string; fullName: string }) => (
    <div className="w-8 h-8 rounded-md overflow-hidden shrink-0">
        <ProfilePhoto userId={userId} fullName={fullName} initialsFontSize={40} />
    </div>
)

const SelectedRow = ({
    avatar,
    label,
    onRemove,
}: {
    avatar: React.ReactNode
    label: string
    onRemove: () => void
}) => (
    <div className="group flex items-center gap-3 py-1.5">
        {avatar}
        <span className="text-white text-sm grow">{label}</span>
        <IconButton className="text-gray-300 opacity-0 group-hover:opacity-100" onClick={onRemove}>
            <Icons.SmallIcon Icon={Icons.PersonRemove} />
        </IconButton>
    </div>
)

const UserSearchDropdown = ({
    placeholder,
    users,
    excludedUserIds,
    onSelectUser,
    onSelectAll,
    onAddEmail,
}: {
    placeholder: string
    users: MemberExtended[]
    excludedUserIds: string[]
    onSelectUser: (user: MemberExtended) => void
    onSelectAll?: (users: MemberExtended[]) => void
    onAddEmail?: (email: string) => void
}) => {
    const [search, setSearch] = useState("")
    const [isOpen, setIsOpen] = useState(false)

    const filtered = users.filter(
        (u) =>
            !excludedUserIds.includes(u.userId) &&
            (u.fullName.toLowerCase().includes(search.toLowerCase()) ||
                u.email.toLowerCase().includes(search.toLowerCase()))
    )

    const isEmail = onAddEmail && search.includes("@") && !search.includes(" ")
    const showSelectAll = onSelectAll && !search && filtered.length > 0
    const hasResults = filtered.length > 0 || isEmail

    return (
        <Dropdown onOpenChange={setIsOpen}>
            <Dropdown.Trigger as="div" clickBehaviour="open" className="w-full">
                <Input
                    autoComplete="off"
                    placeholder={placeholder}
                    value={search}
                    leftItem={<Icons.SmallIcon Icon={Icons.MagnifyingGlass} />}
                    onFocus={() => setIsOpen(true)}
                    onChange={(e) => {
                        setSearch(e.target.value)
                        setIsOpen(true)
                    }}
                />
            </Dropdown.Trigger>
            {isOpen && hasResults && (
                <Dropdown.Menu matchTriggerWidth className="py-2">
                    <ScrollableArea className="max-h-60">
                        {showSelectAll && (
                            <Dropdown.Item
                                className="flex items-center gap-3 px-4 py-2 hover:bg-white-100"
                                onClick={() => onSelectAll(filtered)}
                            >
                                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                                    <Icons.SmallIcon Icon={Icons.Groups} className="text-white" />
                                </div>
                                <span className="text-white text-sm">
                                    All {pluralize({ count: users.length, str: "pilot" })}
                                </span>
                            </Dropdown.Item>
                        )}
                        {filtered.map((u) => (
                            <Dropdown.Item
                                key={u.userId}
                                className="flex items-center gap-3 px-4 py-2 hover:bg-white-100"
                                onClick={() => {
                                    onSelectUser(u)
                                    setSearch("")
                                }}
                            >
                                <UserAvatar userId={u.userId} fullName={u.fullName} />
                                <div className="flex flex-col min-w-0">
                                    <span className="text-white text-sm truncate">
                                        {u.fullName}
                                    </span>
                                    <span className="text-gray-300 text-[12px] truncate">
                                        {u.email}
                                    </span>
                                </div>
                            </Dropdown.Item>
                        ))}
                        {isEmail && (
                            <Dropdown.Item
                                className="flex items-center gap-3 px-4 py-2 hover:bg-white-100"
                                onClick={() => {
                                    onAddEmail(search)
                                    setSearch("")
                                }}
                            >
                                <Icons.SmallIcon Icon={Icons.PersonAdd} className="text-gray-300" />
                                <span className="text-white text-sm">Add “{search}”</span>
                            </Dropdown.Item>
                        )}
                    </ScrollableArea>
                </Dropdown.Menu>
            )}
        </Dropdown>
    )
}

const MetricToggle = ({
    metric,
    onChange,
}: {
    metric: CurrencyPolicyMetric
    onChange: (m: CurrencyPolicyMetric) => void
}) => (
    <div className="flex items-center rounded-lg bg-gray-700 p-1">
        {(["flights", "hours"] as const).map((m) => (
            <button
                key={m}
                type="button"
                className={classNames("px-4 py-1 rounded-md text-sm transition-colors", {
                    "bg-gray-500 text-white": metric === m,
                    "text-gray-300": metric !== m,
                })}
                onClick={() => onChange(m)}
            >
                {m}
            </button>
        ))}
    </div>
)

const NumberInput = ({ value, onChange }: { value: number; onChange: (v: number) => void }) => (
    <div className="w-20">
        <Input
            inputMode="numeric"
            value={value.toString()}
            onChange={(e) => {
                const n = Number(e.target.value)
                if (!isNaN(n)) onChange(n)
            }}
        />
    </div>
)

export function CurrencyPolicyForm() {
    const users = useAppSelector(selectAllRegisteredMembers)
    const dispatch = useAppDispatch()
    const form = useAppSelector(selectForm)
    const [isRenaming, setIsRenaming] = useState(false)
    const [showAllUsers, setShowAllUsers] = useState(false)

    const allUsers = users ?? []
    const selectedUsers = form.userIds
        .map((id) => allUsers.find((u) => u.userId === id))
        .filter(isDefined)

    const recipientLabel = (r: CurrencyPolicyRecipientInput) => {
        if ("email" in r) return r.email
        return allUsers.find((u) => u.userId === r.user_id)?.fullName ?? "Unknown user"
    }

    const removeRecipient = (index: number) =>
        dispatch(
            updateForm({
                key: "recipients",
                value: form.recipients.filter((_, i) => i !== index),
            })
        )

    return (
        <div className="py-6 flex justify-center">
            <div className="flex flex-col w-full max-w-2xl gap-6">
                <ClickAwayListenerV2
                    onClickAway={() => {
                        if (isRenaming) setIsRenaming(false)
                    }}
                >
                    <div
                        className={classNames("w-full", { "cursor-text": !isRenaming })}
                        onClick={() => {
                            if (!isRenaming) setIsRenaming(true)
                        }}
                    >
                        <RenamableText
                            Tag="h2"
                            id="currency-policy-name"
                            title={form.name || "Untitled Policy"}
                            initialTitleIsPlaceholder={!form.name}
                            isRenaming={isRenaming}
                            className="[&_h2]:text-[28px] [&_h2]:text-white"
                            onConfirmRenaming={(_, v) => {
                                dispatch(updateForm({ key: "name", value: v }))
                                setIsRenaming(false)
                            }}
                            onCancelRenaming={() => setIsRenaming(false)}
                        />
                    </div>
                </ClickAwayListenerV2>
                <div className="w-full h-px bg-gray-700" />
                <div className="py-6 flex flex-col gap-6">
                    <FormRow label="Applies to*">
                        <div className="flex flex-col gap-2">
                            <UserSearchDropdown
                                placeholder="Search pilots"
                                users={allUsers}
                                excludedUserIds={form.userIds}
                                onSelectUser={(u) =>
                                    dispatch(
                                        updateForm({
                                            key: "userIds",
                                            value: [...form.userIds, u.userId],
                                        })
                                    )
                                }
                                onSelectAll={(users) =>
                                    dispatch(
                                        updateForm({
                                            key: "userIds",
                                            value: [...form.userIds, ...users.map((u) => u.userId)],
                                        })
                                    )
                                }
                            />
                            <div className="flex flex-col">
                                {(showAllUsers
                                    ? selectedUsers
                                    : selectedUsers.slice(0, COLLAPSED_USER_COUNT)
                                ).map((u) => (
                                    <SelectedRow
                                        key={u.userId}
                                        avatar={
                                            <UserAvatar userId={u.userId} fullName={u.fullName} />
                                        }
                                        label={u.fullName}
                                        onRemove={() =>
                                            dispatch(
                                                updateForm({
                                                    key: "userIds",
                                                    value: form.userIds.filter(
                                                        (id) => id !== u.userId
                                                    ),
                                                })
                                            )
                                        }
                                    />
                                ))}
                                {selectedUsers.length > COLLAPSED_USER_COUNT && (
                                    <button
                                        type="button"
                                        className="self-start text-sm text-gray-300 underline hover:text-white py-1.5"
                                        onClick={() => setShowAllUsers((v) => !v)}
                                    >
                                        {showAllUsers
                                            ? "Collapse"
                                            : `Show ${selectedUsers.length - COLLAPSED_USER_COUNT} more`}
                                    </button>
                                )}
                            </div>
                            {form.userIds.length > 0 && (
                                <button
                                    type="button"
                                    className="self-end text-sm text-gray-300 hover:text-white"
                                    onClick={() =>
                                        dispatch(updateForm({ key: "userIds", value: [] }))
                                    }
                                >
                                    Remove all
                                </button>
                            )}
                        </div>
                    </FormRow>
                    <FormRow label="Drone type*">
                        <DroneTypeMultiSelect
                            value={form.droneTypes}
                            onChange={(v) => dispatch(updateForm({ key: "droneTypes", value: v }))}
                        />
                    </FormRow>
                    <FormRow label="Conditions*">
                        <div className="flex flex-col gap-3">
                            <div className="flex items-center gap-3">
                                <h5 className="text-gray-100 w-28">Requirement</h5>
                                <NumberInput
                                    value={form.threshold}
                                    onChange={(v) =>
                                        dispatch(updateForm({ key: "threshold", value: v }))
                                    }
                                />
                                <MetricToggle
                                    metric={form.metric}
                                    onChange={(m) =>
                                        dispatch(updateForm({ key: "metric", value: m }))
                                    }
                                />
                            </div>
                            <div className="flex items-center gap-3">
                                <h5 className="text-gray-100 w-28">Period</h5>
                                <NumberInput
                                    value={form.windowDays}
                                    onChange={(v) =>
                                        dispatch(updateForm({ key: "windowDays", value: v }))
                                    }
                                />
                                <h5 className="text-gray-300">days</h5>
                            </div>
                        </div>
                    </FormRow>
                    <FormRow label="Warning threshold*">
                        <div className="flex items-center gap-3">
                            <h5 className="text-gray-100">Warn pilots</h5>
                            <NumberInput
                                value={form.warningDays}
                                onChange={(v) =>
                                    dispatch(updateForm({ key: "warningDays", value: v }))
                                }
                            />
                            <h5 className="text-gray-300">days before expiry</h5>
                        </div>
                    </FormRow>
                    <FormRow label="Email reminder">
                        <div className="pt-2">
                            <ToggleSwitch
                                isToggled={form.emailReminder}
                                setIsToggled={(v) =>
                                    dispatch(updateForm({ key: "emailReminder", value: v }))
                                }
                            />
                        </div>
                    </FormRow>
                    {form.emailReminder && (
                        <FormRow label="Recipients">
                            <div className="flex flex-col gap-2">
                                <UserSearchDropdown
                                    placeholder="Search pilots or add an email"
                                    users={allUsers}
                                    excludedUserIds={form.recipients.flatMap((r) =>
                                        "user_id" in r ? [r.user_id] : []
                                    )}
                                    onSelectUser={(u) =>
                                        dispatch(
                                            updateForm({
                                                key: "recipients",
                                                value: [...form.recipients, { user_id: u.userId }],
                                            })
                                        )
                                    }
                                    onAddEmail={(email) =>
                                        dispatch(
                                            updateForm({
                                                key: "recipients",
                                                value: [...form.recipients, { email }],
                                            })
                                        )
                                    }
                                />
                                <div
                                    className="flex items-center gap-3 py-1.5 cursor-pointer"
                                    onClick={() =>
                                        dispatch(
                                            updateForm({
                                                key: "notifyAllUsers",
                                                value: !form.notifyAllUsers,
                                            })
                                        )
                                    }
                                >
                                    <Checkbox isChecked={form.notifyAllUsers} onClick={() => {}} />
                                    <span className="text-gray-300 text-sm">
                                        Every pilot in the policy
                                    </span>
                                </div>
                                <div className="flex flex-col">
                                    {form.recipients.map((r, i) => (
                                        <SelectedRow
                                            key={"user_id" in r ? r.user_id : r.email}
                                            avatar={
                                                "user_id" in r ? (
                                                    <UserAvatar
                                                        userId={r.user_id}
                                                        fullName={recipientLabel(r)}
                                                    />
                                                ) : (
                                                    <Icons.SmallIcon
                                                        Icon={Icons.PersonAdd}
                                                        className="text-gray-300"
                                                    />
                                                )
                                            }
                                            label={recipientLabel(r)}
                                            onRemove={() => removeRecipient(i)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </FormRow>
                    )}
                </div>
                <div className="w-full h-px bg-gray-700" />
                <div className="text-gray-300 text-sm">
                    A pilot must log {form.threshold} {form.metric} within any {form.windowDays} day
                    period.
                    <br />
                    They{"'"}ll be warned {form.warningDays} days before their currency expires.
                </div>
            </div>
        </div>
    )
}
